// The card options modal's choices, as pure functions (spec
// docs/superpowers/specs/2026-11-02-wantlist-redesign-design.md, "Card
// options modal"). A Default Want List card's Card Specificity (CONTEXT.md)
// is its name plus an optional set, printing (set + collector number) and
// finish; the modal offers a finish chip row, a set dropdown and a grid of
// printings, and each pick re-points the entry, narrowing or broadening.
// What a choice can't apply to stays in place, greyed (VISUALS.md
// "Unavailable choices are greyed, not hidden").

/** @typedef {import('../types.js').CardSet} CardSet */

/**
 * An entry's Card Specificity as the modal reads and writes it: lower-case
 * set code, null for every wildcard.
 * @typedef {{ set_code: string | null, collector_number: string | null, finish: string | null }} Spec
 */

/**
 * @param {import('../types.js').WantListEntry} entry
 * @returns {Spec}
 */
export function specOf(entry) {
  return {
    set_code: entry.set_code ? String(entry.set_code).toLowerCase() : null,
    collector_number: entry.collector_number || null,
    finish: entry.finish || null,
  };
}

/** @typedef {{ key: string | null, label: string, on: boolean, disabled: boolean }} FinishChip */

/**
 * Each finish's name, everywhere the want page shows one (the table's Foil
 * column and phone mark, the options' chips).
 * @type {Readonly<Record<string, string>>}
 */
export const FINISH_LABELS = Object.freeze({
  nonfoil: 'Non-foil',
  foil: 'Foil',
  etched: 'Etched',
});

/**
 * The four finish chips (a small fixed set, so pills: VISUALS.md).
 * @type {ReadonlyArray<{ key: string | null, label: string }>}
 */
const FINISHES = [
  { key: null, label: 'Any' },
  ...['nonfoil', 'foil', 'etched'].map((key) => ({ key, label: FINISH_LABELS[key] })),
];

/**
 * The finish chips: a finish is greyed when nothing in reach comes in it:
 * the pinned printing (once the grid shows it), else the picked set, else
 * any set. Until the sets are in (`null`) nothing is greyed; the current
 * finish never is.
 * @param {Spec} spec
 * @param {CardSet[] | null} sets
 * @param {Pick<PrintingTile, 'on' | 'finishes'>[]} [tiles] - the grid
 * @returns {FinishChip[]}
 */
export function finishChips(spec, sets, tiles = []) {
  const pinned = spec.collector_number ? tiles.find((t) => t.on) : undefined;
  const inSet = sets && (spec.set_code ? sets.filter((s) => s.set_code === spec.set_code) : sets);
  const offered = pinned
    ? new Set(pinned.finishes)
    : inSet
      ? new Set(inSet.flatMap((s) => s.finishes))
      : null;
  return FINISHES.map(({ key, label }) => {
    const on = key === spec.finish;
    return { key, label, on, disabled: !!key && !on && !!offered && !offered.has(key) };
  });
}

/**
 * @typedef {{ code: string, name: string, year: string | null, rarity: string | null,
 *   on: boolean, disabled: boolean }} SetOption
 */

/**
 * The set dropdown's options, in the order given (the server's, newest
 * first). A set with no printing in the picked finish is greyed; the picked
 * set never is.
 * @param {Spec} spec
 * @param {CardSet[]} sets
 * @returns {SetOption[]}
 */
export function setOptions(spec, sets) {
  return sets.map((s) => {
    const code = s.set_code;
    const on = code === spec.set_code;
    return {
      code,
      name: s.set_name || code.toUpperCase(),
      year: s.released_at ? String(s.released_at).slice(0, 4) : null,
      rarity: s.rarity ?? null,
      on,
      disabled: !on && !!spec.finish && !s.finishes.includes(spec.finish),
    };
  });
}

/**
 * The search box's narrowing: the options whose set name or code holds
 * `query` anywhere, ignoring case (a blank query keeps the list as it is).
 * @param {SetOption[]} options
 * @param {string} query
 * @returns {SetOption[]}
 */
export function filterSets(options, query) {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter((o) => o.code.includes(q) || o.name.toLowerCase().includes(q));
}

/**
 * @typedef {{ key: string, set: string, cn: string, setName: string,
 *   scryfallId: string, variant: string | null, finishes: string[], price: number | null, on: boolean,
 *   disabled: boolean }} PrintingTile
 */

/**
 * The printings grid: one tile per printing (the read sends a row per
 * finish), in the order read. A tile is priced in the picked finish, else
 * in its first finish (nonfoil first). A printing not made in the picked
 * finish is greyed in its place, never dropped (the grid is not refilled);
 * the pinned one is `on`.
 * @param {Spec} spec
 * @param {import('../types.js').Printing[]} rows
 * @returns {PrintingTile[]}
 */
export function printingTiles(spec, rows) {
  /** @type {Map<string, PrintingTile>} */
  const tiles = new Map();
  /** @type {Map<string, Record<string, number | null>>} */
  const prices = new Map();
  for (const r of rows) {
    const set = String(r.set_code).toLowerCase();
    const key = `${set}/${r.collector_number}`;
    let tile = tiles.get(key);
    if (!tile) {
      tile = {
        key,
        set,
        cn: r.collector_number,
        setName: r.set_name || set.toUpperCase(),
        scryfallId: r.scryfall_id,
        variant: r.variant ?? null,
        finishes: [],
        price: null,
        on: set === spec.set_code && r.collector_number === spec.collector_number,
        disabled: false,
      };
      tiles.set(key, tile);
      prices.set(key, {});
    }
    tile.finishes.push(r.finish);
    /** @type {Record<string, number | null>} */ (prices.get(key))[r.finish] = r.price;
  }
  for (const tile of tiles.values()) {
    const byFinish = /** @type {Record<string, number | null>} */ (prices.get(tile.key));
    const made = !spec.finish || tile.finishes.includes(spec.finish);
    tile.price = byFinish[made && spec.finish ? spec.finish : tile.finishes[0]] ?? null;
    tile.disabled = !made && !tile.on;
  }
  return [...tiles.values()];
}

/**
 * `next`, or null when it is the specificity already held (nothing to send).
 * @param {Spec} spec
 * @param {Spec} next
 * @returns {Spec | null}
 */
function changed(spec, next) {
  const same =
    next.set_code === spec.set_code &&
    next.collector_number === spec.collector_number &&
    next.finish === spec.finish;
  return same ? null : next;
}

/**
 * A set from the dropdown: any printing in it, the finish kept (null, Any
 * set, broadens to every set). Picking the pinned printing's own set widens
 * the pin to the whole set.
 * @param {Spec} spec
 * @param {string | null} code
 * @returns {Spec | null}
 */
export function pickSet(spec, code) {
  return changed(spec, { set_code: code, collector_number: null, finish: spec.finish });
}

/**
 * A printing from the grid: that exact printing, the finish kept. Tapping
 * the pinned printing again widens to its set.
 * @param {Spec} spec
 * @param {PrintingTile} tile
 * @returns {Spec}
 */
export function pickPrinting(spec, tile) {
  return { set_code: tile.set, collector_number: tile.on ? null : tile.cn, finish: spec.finish };
}

/**
 * A finish chip (null, Any, broadens): the set and the pinned printing
 * stay; a finish they rule out is a greyed chip (finishChips), never a pick.
 * @param {Spec} spec
 * @param {string | null} finish
 * @returns {Spec | null}
 */
export function pickFinish(spec, finish) {
  return changed(spec, { ...spec, finish });
}
